import type { ProvisioningStatus } from '@/features/projects/components/project-provisioning-state'
import type { Project, ProjectMember } from '@/shared/types/domain'

/**
 * Проекты без статуса — обычные: поле появилось вместе с автосборкой, и старые
 * записи (как и ответы кеша) не должны из-за него уезжать в заглушку.
 */
export function getProjectProvisioningState(project: Project): ProvisioningStatus | null {
  const status = project.provisioning_status

  return status === 'provisioning' || status === 'blocked' ? status : null
}

export function getProjectPromptsCount(project: Project, fallback = 0): number {
  return project.prompts_count ?? project.prompt_count ?? project.prompts?.length ?? fallback
}

export function hasProjectPromptsCount(project: Project): boolean {
  return (
    project.prompts_count !== undefined ||
    project.prompt_count !== undefined ||
    project.prompts !== undefined
  )
}

export function getProjectMemberUserId(member: ProjectMember): number | string | undefined {
  return member.user_id ?? member.userId ?? member.user?.id ?? member.id
}

export function getProjectMemberName(member: ProjectMember): string {
  const userId = getProjectMemberUserId(member)

  return (
    member.user?.name || member.name || (userId !== undefined ? `User #${userId}` : 'Пользователь')
  )
}

export function getProjectMemberEmail(member: ProjectMember): string {
  return member.user?.email || member.email || '—'
}

export function getArchiveBadgeLabel(project: Project): string | null {
  if (!project.archived_at) return null

  return project.archived_reason?.startsWith('auto_') ? 'В архиве автоматически' : 'В архиве'
}

const INACTIVITY_BADGE_THRESHOLD_DAYS = 30

/**
 * UI-эвристика для живых карточек — не связана с порогами автоархива на бэкенде
 * (auto_archive_empty_days / project_auto_archive_days), хотя число намеренно совпадает
 * с порогом автоархива «пустышек» (`config('projects.auto_archive_empty_days')`) —
 * месяц тишины читается как повод задуматься. Совпадение не синхронизируется
 * автоматически: если бэкенд-порог поменяют, эту константу трогать не обязательно.
 * Подсказывает глазами, что диалогов давно не было, чтобы такой проект забрали в
 * массовую архивацию раньше, чем до него доберётся ночной автоархив.
 */
export function getInactivityDaysBadge(project: Project, now: Date = new Date()): number | null {
  if (project.archived_at || !project.last_activity_at) return null

  const days = Math.floor(
    (now.getTime() - new Date(project.last_activity_at).getTime()) / (24 * 60 * 60 * 1000),
  )

  return days >= INACTIVITY_BADGE_THRESHOLD_DAYS ? days : null
}
